import localStorageService from 'services/local-storage-service';
import { TKioskSession } from 'types/kiosk/TKioskSession';
import Lang from 'types/Lang';
import { toKioskLang } from 'utils/kiosk-language';

const TOKEN_KEY = 'kioskToken';
const SESSION_KEY = 'kioskSession';
const DEVICE_ID_KEY = 'kioskDeviceIdentifier';
const PENDING_LANGUAGE_KEY = 'kioskPendingLanguage';

const generateUuid = (): string => (crypto as unknown as { randomUUID: () => string }).randomUUID();

const OS_MAX_LENGTH = 100;

/**
 * Compact, human-readable "browser major-version / OS" string for the activation audit log
 * (e.g. "Chrome 149 / macOS"). The raw user agent is far too long for the backend's
 * StringLength(100) column, so we parse it down and cap it defensively.
 */
const getBrowserAndOs = (): string => {
  if (typeof navigator === 'undefined') {
    return '';
  }
  const ua = navigator.userAgent;

  // Order matters: Edge/Opera UAs also contain "Chrome/", so match those first.
  const browserMatchers: { name: string; regex: RegExp }[] = [
    { name: 'Edge', regex: /Edg\/(\d+)/ },
    { name: 'Opera', regex: /OPR\/(\d+)/ },
    { name: 'Chrome', regex: /Chrome\/(\d+)/ },
    { name: 'Firefox', regex: /Firefox\/(\d+)/ },
    { name: 'Safari', regex: /Version\/(\d+)[\d.]*\s+Safari/ },
  ];
  let browser = 'Unknown';
  const matcher = browserMatchers.find(b => b.regex.test(ua));
  if (matcher) {
    const match = ua.match(matcher.regex);
    browser = match?.[1] ? `${matcher.name} ${match[1]}` : matcher.name;
  }

  let os = 'Unknown';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/(iPhone|iPad|iPod)/.test(ua)) os = 'iOS';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} / ${os}`.slice(0, OS_MAX_LENGTH);
};

/**
 * Kiosk session lifecycle + persistence. The kiosk token is the durable source of truth;
 * app state is re-derived from it on every launch.
 */
const kioskSessionService = {
  /** Stable per-device id; generated once and reused so "one kiosk per device" holds. */
  getOrCreateDeviceIdentifier: (): string => {
    const existing = localStorageService.getItem<string>(DEVICE_ID_KEY);
    if (existing && typeof existing === 'string') {
      return existing;
    }
    const generated = generateUuid();
    localStorageService.setItem({ kioskDeviceIdentifier: generated });
    return generated;
  },

  getToken: (): string | null => {
    const token = localStorageService.getItem<string>(TOKEN_KEY);
    return token && typeof token === 'string' ? token : null;
  },

  getCachedSession: (): TKioskSession | null => {
    const session = localStorageService.getItem<TKioskSession>(SESSION_KEY);
    return session && typeof session === 'object' ? (session as TKioskSession) : null;
  },

  persist: (token: string, session: TKioskSession): void => {
    localStorageService.setItem({ kioskToken: token, kioskSession: session });
  },

  /** Language picked on this kiosk but not yet confirmed saved on the server; null when none. */
  getPendingLanguage: (): Lang | null => {
    try {
      return toKioskLang(localStorageService.getItem<string>(PENDING_LANGUAGE_KEY) as string);
    } catch {
      return null;
    }
  },

  /**
   * Best effort: if storage is unavailable the language still applies for this session. Also updates the
   * cached session, so a reload starts in this language before the server answers.
   */
  setPendingLanguage: (lang: Lang): void => {
    try {
      localStorageService.setItem({ kioskPendingLanguage: lang });
      const session = kioskSessionService.getCachedSession();
      if (session) {
        localStorageService.setItem({ kioskSession: { ...session, language: lang } });
      }
    } catch {
      // Storage blocked / full — never block the employee over a language preference.
    }
  },

  /** The language a reloading kiosk should start in (unsaved pick, else the cached session's); null if not a kiosk. */
  getStartupLanguage: (): Lang | null => {
    if (!kioskSessionService.getToken()) return null;
    return kioskSessionService.getPendingLanguage() ?? toKioskLang(kioskSessionService.getCachedSession()?.language);
  },

  clearPendingLanguage: (): void => {
    try {
      localStorageService.removeItem(PENDING_LANGUAGE_KEY);
    } catch {
      // See setPendingLanguage.
    }
  },

  clear: (): void => {
    localStorageService.removeItem(TOKEN_KEY);
    localStorageService.removeItem(SESSION_KEY);
    localStorageService.removeItem(PENDING_LANGUAGE_KEY);
  },

  /** Diagnostic-only "browser / OS" string sent on activation (capped at 100 chars). */
  getOperatingSystem: (): string => getBrowserAndOs(),
};

export default kioskSessionService;
